import React, { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Copy, X, Plus, Power, Pencil, QrCode, Trash2, Download } from "lucide-react";
import {
  adminListCustomCodes,
  adminCreateCustomCode,
  adminPatchCustomCode,
  adminDisableCustomCode,
  adminHardDeleteCustomCode,
  adminGetMagicLink,
} from "../api/referralApi";
import { formatApiError } from "../api/client";


/**
 * Format a rate fraction (e.g. 0.234) as a percentage with 1 decimal,
 * or the string "—" when the rate is null (denominator was 0). This is
 * the single source of truth for funnel-rate rendering across the page.
 */
function fmtPct(r) {
  if (r === null || r === undefined) return "—";
  return `${(r * 100).toFixed(1)}%`;
}


function FunnelTooltip({ row }) {
  const stages = [
    { label: "Klicks", count: row.clicks_count ?? 0 },
    {
      label: "Registrierungen",
      count: row.registrations_count ?? 0,
      rate: row.click_to_reg_rate,
    },
    {
      label: "Verifiziert",
      count: row.email_confirmed_count ?? 0,
      rate: row.reg_to_verified_rate,
    },
    {
      label: "Pro User",
      count: row.pro_users_count ?? 0,
      rate: row.verified_to_pro_rate,
    },
  ];
  return (
    <div
      className="absolute left-0 top-full mt-2 z-20 w-[260px] rounded-lg border border-zinc-700 bg-zinc-950 p-3 text-zinc-200 shadow-xl pointer-events-none"
      data-testid={`funnel-tooltip-${row.code}`}
    >
      <div className="text-xs font-mono text-emerald-300 mb-2 tabular-nums">
        {row.code}
      </div>
      <div className="h-px bg-zinc-800 mb-2" />
      {stages.map((s, idx) => (
        <div key={s.label}>
          {idx > 0 && (
            <div className="text-[10px] text-zinc-500 text-center py-0.5">
              ↓ {fmtPct(s.rate)}
            </div>
          )}
          <div className="flex items-center justify-between text-xs">
            <span className="text-zinc-400">{s.label}</span>
            <span className="tabular-nums text-zinc-100">{s.count}</span>
          </div>
        </div>
      ))}
      <div className="h-px bg-zinc-800 my-2" />
      <div className="flex items-center justify-between text-[11px]">
        <span className="text-zinc-500">Overall (Klick → Pro)</span>
        <span className="tabular-nums text-amber-300 font-medium">
          {fmtPct(row.click_to_pro_rate)}
        </span>
      </div>
    </div>
  );
}


function SortableHeader({ label, sortKey, currentSort, setSort, align = "right" }) {
  const isActive = currentSort === sortKey;
  return (
    <button
      type="button"
      onClick={() => setSort(sortKey)}
      data-testid={`sort-${sortKey}`}
      className={`inline-flex items-center gap-1 hover:text-zinc-200 transition-colors ${
        isActive ? "text-emerald-300" : "text-zinc-500"
      } ${align === "right" ? "ml-auto" : ""}`}
    >
      <span>{label}</span>
      {isActive && <span className="text-[9px]">↓</span>}
    </button>
  );
}

/**
 * Phase V1.5 — Admin Vanity Referral Codes Page.
 *
 * Lets the admin generate marketing codes (KOSMO, VIENNA-2026, …),
 * see registration/conversion counters per code, and disable codes
 * that have run their course. These codes do NOT create a
 * `referred_by_user_id` binding — they only bump a marketing counter.
 */

function StatusPill({ status }) {
  const isActive = status === "active";
  return (
    <span
      className={`inline-block rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-wider ${
        isActive
          ? "border-emerald-500/30 text-emerald-300 bg-emerald-500/10"
          : "border-zinc-700 text-zinc-400 bg-zinc-800/40"
      }`}
      data-testid={`code-status-${status}`}
    >
      {status}
    </span>
  );
}

function CodeCell({ code }) {
  const onCopy = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(code);
      toast.success(`${code} kopiert`);
    } catch {
      toast.error("Kopieren fehlgeschlagen");
    }
  };
  return (
    <div className="inline-flex items-center gap-2">
      <code className="rounded bg-zinc-900/80 border border-zinc-800 px-2 py-0.5 text-sm font-mono text-emerald-300">
        {code}
      </code>
      <button
        type="button"
        onClick={onCopy}
        data-testid={`copy-code-${code}`}
        className="text-zinc-500 hover:text-zinc-200 transition-colors"
        title="Code kopieren"
      >
        <Copy className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

function NewCodeModal({ open, onClose }) {
  const qc = useQueryClient();
  const [code, setCode] = useState("");
  const [ownerName, setOwnerName] = useState("");
  const [ownerEmail, setOwnerEmail] = useState("");
  const [note, setNote] = useState("");

  const reset = () => {
    setCode("");
    setOwnerName("");
    setOwnerEmail("");
    setNote("");
  };

  const mutation = useMutation({
    mutationFn: adminCreateCustomCode,
    onSuccess: (created) => {
      toast.success(`Code ${created.code} erstellt`);
      qc.invalidateQueries({ queryKey: ["admin", "custom-codes"] });
      reset();
      onClose();
    },
    onError: (e) => toast.error(formatApiError(e)),
  });

  if (!open) return null;

  const submit = (e) => {
    e.preventDefault();
    if (!code.trim() || !ownerName.trim()) {
      toast.error("Code und Owner-Name sind Pflicht");
      return;
    }
    mutation.mutate({
      code: code.trim().toUpperCase(),
      owner_name: ownerName.trim(),
      owner_email: ownerEmail.trim() || null,
      note: note.trim() || null,
    });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/70 backdrop-blur-sm overflow-y-auto p-4"
      onClick={onClose}
      data-testid="new-code-modal-backdrop"
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="mt-16 w-full max-w-lg rounded-2xl border border-zinc-800 bg-zinc-950 shadow-2xl"
        data-testid="new-code-modal"
      >
        <div className="flex items-center justify-between border-b border-zinc-800 px-6 py-4">
          <h2 className="text-lg font-semibold text-zinc-100">Neuer Custom-Code</h2>
          <button
            type="button"
            onClick={onClose}
            data-testid="new-code-modal-close"
            className="text-zinc-500 hover:text-zinc-200"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="space-y-4 px-6 py-5">
          <div>
            <label className="block text-xs uppercase tracking-wider text-zinc-400 mb-1">
              Code (2–32 Zeichen, A–Z / 0–9 / _-)
            </label>
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="KOSMO oder VIENNA-2026"
              data-testid="new-code-input-code"
              autoFocus
              className="w-full rounded-md border border-zinc-800 bg-zinc-900/60 px-3 py-2 text-sm font-mono text-emerald-300 placeholder-zinc-600 focus:border-emerald-500 focus:outline-none uppercase"
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-zinc-400 mb-1">
              Owner-Name
            </label>
            <input
              type="text"
              value={ownerName}
              onChange={(e) => setOwnerName(e.target.value)}
              placeholder="z.B. Kosmo Influencer"
              data-testid="new-code-input-owner-name"
              className="w-full rounded-md border border-zinc-800 bg-zinc-900/60 px-3 py-2 text-sm text-zinc-100 placeholder-zinc-600 focus:border-emerald-500 focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-zinc-400 mb-1">
              Owner-Email (optional)
            </label>
            <input
              type="email"
              value={ownerEmail}
              onChange={(e) => setOwnerEmail(e.target.value)}
              placeholder="kosmo@example.com"
              data-testid="new-code-input-owner-email"
              className="w-full rounded-md border border-zinc-800 bg-zinc-900/60 px-3 py-2 text-sm text-zinc-100 placeholder-zinc-600 focus:border-emerald-500 focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-zinc-400 mb-1">
              Notiz (optional)
            </label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Kontext / Kampagne / Ablaufdatum…"
              data-testid="new-code-input-note"
              rows={3}
              className="w-full rounded-md border border-zinc-800 bg-zinc-900/60 px-3 py-2 text-sm text-zinc-100 placeholder-zinc-600 focus:border-emerald-500 focus:outline-none resize-none"
            />
          </div>
        </div>
        <div className="flex items-center justify-end gap-3 border-t border-zinc-800 px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            data-testid="new-code-cancel"
            className="rounded-md px-4 py-2 text-sm text-zinc-400 hover:text-zinc-100"
          >
            Abbrechen
          </button>
          <button
            type="submit"
            disabled={mutation.isPending}
            data-testid="new-code-submit"
            className="rounded-md bg-emerald-500/90 hover:bg-emerald-400 disabled:opacity-50 disabled:cursor-not-allowed px-4 py-2 text-sm font-medium text-zinc-950 transition-colors"
          >
            {mutation.isPending ? "Wird erstellt…" : "Erstellen"}
          </button>
        </div>
      </form>
    </div>
  );
}

function EditNoteModal({ open, code, initialNote, onClose }) {
  const qc = useQueryClient();
  const [note, setNote] = useState(initialNote || "");
  React.useEffect(() => setNote(initialNote || ""), [initialNote]);

  const mutation = useMutation({
    mutationFn: (n) => adminPatchCustomCode(code, { note: n || null }),
    onSuccess: () => {
      toast.success("Notiz aktualisiert");
      qc.invalidateQueries({ queryKey: ["admin", "custom-codes"] });
      onClose();
    },
    onError: (e) => toast.error(formatApiError(e)),
  });

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="edit-note-modal-backdrop"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="mt-24 w-full max-w-md rounded-2xl border border-zinc-800 bg-zinc-950 shadow-2xl"
        data-testid="edit-note-modal"
      >
        <div className="flex items-center justify-between border-b border-zinc-800 px-5 py-3">
          <h3 className="text-sm font-semibold text-zinc-100">
            Notiz für <code className="text-emerald-300 font-mono">{code}</code>
          </h3>
          <button
            type="button"
            onClick={onClose}
            data-testid="edit-note-close"
            className="text-zinc-500 hover:text-zinc-200"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="px-5 py-4">
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            data-testid="edit-note-textarea"
            rows={4}
            className="w-full rounded-md border border-zinc-800 bg-zinc-900/60 px-3 py-2 text-sm text-zinc-100 focus:border-emerald-500 focus:outline-none resize-none"
          />
        </div>
        <div className="flex items-center justify-end gap-3 border-t border-zinc-800 px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md px-3 py-1.5 text-sm text-zinc-400 hover:text-zinc-100"
          >
            Abbrechen
          </button>
          <button
            type="button"
            onClick={() => mutation.mutate(note)}
            disabled={mutation.isPending}
            data-testid="edit-note-save"
            className="rounded-md bg-emerald-500/90 hover:bg-emerald-400 disabled:opacity-50 px-3 py-1.5 text-sm font-medium text-zinc-950"
          >
            {mutation.isPending ? "…" : "Speichern"}
          </button>
        </div>
      </div>
    </div>
  );
}

function MagicLinkModal({ open, code, onClose }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ["admin", "custom-code-magic-link", code],
    queryFn: () => adminGetMagicLink(code, 320),
    enabled: open && !!code,
  });

  if (!open) return null;

  const onCopyLink = async () => {
    if (!data?.link) return;
    try {
      await navigator.clipboard.writeText(data.link);
      toast.success("Link kopiert");
    } catch {
      toast.error("Kopieren fehlgeschlagen");
    }
  };

  const onDownloadQR = () => {
    if (!data?.qr_data_uri) return;
    const a = document.createElement("a");
    a.href = data.qr_data_uri;
    a.download = `dealalert-${code}-qr.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/70 backdrop-blur-sm p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="magic-link-modal-backdrop"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="mt-12 w-full max-w-md rounded-2xl border border-zinc-800 bg-zinc-950 shadow-2xl"
        data-testid="magic-link-modal"
      >
        <div className="flex items-center justify-between border-b border-zinc-800 px-5 py-3">
          <h3 className="text-sm font-semibold text-zinc-100">
            Magic Link für <code className="text-emerald-300 font-mono">{code}</code>
          </h3>
          <button
            type="button"
            onClick={onClose}
            data-testid="magic-link-close"
            className="text-zinc-500 hover:text-zinc-200"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="px-5 py-5 space-y-4">
          {isLoading && (
            <div className="text-center text-sm text-zinc-500 py-8">
              Lade Magic Link…
            </div>
          )}
          {error && (
            <div className="text-sm text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-md p-3">
              {formatApiError(error)}
            </div>
          )}
          {data && (
            <>
              <div>
                <label className="block text-xs uppercase tracking-wider text-zinc-400 mb-1.5">
                  Shareable Link
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    readOnly
                    value={data.link}
                    data-testid="magic-link-url"
                    className="flex-1 rounded-md border border-zinc-800 bg-zinc-900/60 px-3 py-2 text-sm text-emerald-300 font-mono focus:border-emerald-500 focus:outline-none"
                  />
                  <button
                    type="button"
                    onClick={onCopyLink}
                    data-testid="magic-link-copy"
                    className="rounded-md border border-zinc-800 bg-zinc-900/60 px-3 text-zinc-300 hover:text-emerald-300 hover:border-emerald-500/50 transition-colors"
                    title="Link kopieren"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <div className="flex flex-col items-center">
                <div className="rounded-xl bg-white p-3" data-testid="magic-link-qr-wrapper">
                  <img
                    src={data.qr_data_uri}
                    alt={`QR-Code für ${code}`}
                    data-testid="magic-link-qr-img"
                    className="block w-[256px] h-[256px]"
                  />
                </div>
                <button
                  type="button"
                  onClick={onDownloadQR}
                  data-testid="magic-link-qr-download"
                  className="mt-3 inline-flex items-center gap-2 text-xs text-emerald-400 hover:text-emerald-300 underline-offset-4 hover:underline"
                >
                  <Download className="w-3.5 h-3.5" />
                  QR als PNG herunterladen
                </button>
              </div>
              {data.hint && (
                <p className="text-[11px] text-zinc-500 text-center italic">
                  {data.hint}
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}


export default function AdminReferralCodesPage() {
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState("");
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("created_desc");
  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [magicLinkCode, setMagicLinkCode] = useState(null);

  const params = useMemo(
    () => ({
      ...(statusFilter ? { status: statusFilter } : {}),
      ...(q ? { q } : {}),
      sort,
      limit: 100,
    }),
    [statusFilter, q, sort],
  );

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "custom-codes", params],
    queryFn: () => adminListCustomCodes(params),
  });

  const toggleMutation = useMutation({
    mutationFn: async ({ code, currentStatus }) => {
      if (currentStatus === "active") {
        await adminDisableCustomCode(code);
      } else {
        await adminPatchCustomCode(code, { status: "active" });
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "custom-codes"] });
      toast.success("Status geändert");
    },
    onError: (e) => toast.error(formatApiError(e)),
  });

  const hardDeleteMutation = useMutation({
    mutationFn: adminHardDeleteCustomCode,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "custom-codes"] });
      toast.success("Code gelöscht");
    },
    onError: (e) => toast.error(formatApiError(e)),
  });

  const items = data?.items || [];
  const total = data?.total ?? items.length;

  return (
    <div className="space-y-5" data-testid="admin-referral-codes-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-zinc-100">Referral Codes</h1>
          <p className="text-sm text-zinc-500">
            Manuelle Marketing-Codes (KOSMO, VIENNA, …). Werden bei Registration
            erkannt und zählen Anmeldungen, ohne Provision auszulösen.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setModalOpen(true)}
          data-testid="new-code-button"
          className="inline-flex items-center gap-2 rounded-md bg-emerald-500/90 hover:bg-emerald-400 px-4 py-2 text-sm font-medium text-zinc-950 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Neuer Code
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-zinc-800 bg-zinc-900/40 p-3">
        <input
          type="text"
          placeholder="Code oder Owner suchen…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="filter-search"
          className="flex-1 min-w-[200px] rounded-md border border-zinc-800 bg-zinc-900/60 px-3 py-2 text-sm text-zinc-100 placeholder-zinc-500 focus:border-emerald-500 focus:outline-none"
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          data-testid="filter-status"
          className="rounded-md border border-zinc-800 bg-zinc-900/60 px-3 py-2 text-sm text-zinc-100 focus:border-emerald-500 focus:outline-none"
        >
          <option value="">Alle Status</option>
          <option value="active">Active</option>
          <option value="disabled">Disabled</option>
        </select>
        <select
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          data-testid="filter-sort"
          className="rounded-md border border-zinc-800 bg-zinc-900/60 px-3 py-2 text-sm text-zinc-100 focus:border-emerald-500 focus:outline-none"
        >
          <option value="created_desc">Neueste zuerst</option>
          <option value="created_asc">Älteste zuerst</option>
          <option value="clicks_desc">Meiste Klicks</option>
          <option value="registrations_desc">Meiste Anmeldungen</option>
          <option value="verified_desc">Meiste Verifizierte</option>
          <option value="pro_desc">Meiste Pro User</option>
          <option value="conv_rate_desc">Beste Conv-Rate</option>
          <option value="roi_rank">ROI-Ranking (≥5 Klicks)</option>
          <option value="code_asc">Code A→Z</option>
        </select>
        <span className="ml-auto text-xs text-zinc-500" data-testid="total-count">
          {total} {total === 1 ? "Code" : "Codes"}
        </span>
      </div>

      {/* Table */}
      <div className="rounded-2xl border border-zinc-800 bg-zinc-900/40 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="codes-table">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
                <th className="px-4 py-3">Code</th>
                <th className="px-4 py-3">Owner</th>
                <th className="px-4 py-3 text-right">
                  <SortableHeader label="Klicks" sortKey="clicks_desc" currentSort={sort} setSort={setSort} />
                </th>
                <th className="px-4 py-3 text-right">
                  <SortableHeader label="Reg." sortKey="registrations_desc" currentSort={sort} setSort={setSort} />
                </th>
                <th className="px-4 py-3 text-right">
                  <SortableHeader label="Verifiziert" sortKey="verified_desc" currentSort={sort} setSort={setSort} />
                </th>
                <th className="px-4 py-3 text-right">
                  <SortableHeader label="Pro" sortKey="pro_desc" currentSort={sort} setSort={setSort} />
                </th>
                <th className="px-4 py-3 text-right">
                  <SortableHeader label="Conv %" sortKey="conv_rate_desc" currentSort={sort} setSort={setSort} />
                </th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={9} className="px-4 py-8 text-center text-zinc-500">
                    Lade Codes…
                  </td>
                </tr>
              )}
              {!isLoading && items.length === 0 && (
                <tr>
                  <td
                    colSpan={9}
                    className="px-4 py-12 text-center text-zinc-500"
                    data-testid="empty-state"
                  >
                    Noch keine Codes. Erstelle deinen ersten Marketing-Code.
                  </td>
                </tr>
              )}
              {items.map((row) => (
                <tr
                  key={row.ulid}
                  data-testid={`code-row-${row.code}`}
                  className="border-b border-zinc-800/60 hover:bg-zinc-900/30 transition-colors"
                >
                  <td className="px-4 py-3">
                    {/* Funnel tooltip on Code-Cell hover. Uses a
                        `group` wrapper + `group-hover` on the absolute
                        tooltip so we don't need JS state. */}
                    <div className="group relative inline-block">
                      <CodeCell code={row.code} />
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                        <FunnelTooltip row={row} />
                      </div>
                    </div>
                    {row.note && (
                      <div
                        className="text-[11px] text-zinc-500 mt-1 max-w-xs truncate"
                        title={row.note}
                      >
                        {row.note}
                      </div>
                    )}
                    <div className="text-[10px] text-zinc-600 mt-0.5">
                      {row.owner_email || ""}
                      {row.created_at && (
                        <span className="ml-2">
                          · {new Date(row.created_at).toLocaleDateString("de-DE")}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-zinc-200">{row.owner_name}</td>
                  <td
                    className="px-4 py-3 text-right tabular-nums text-zinc-100"
                    data-testid={`clicks-${row.code}`}
                  >
                    {row.clicks_count ?? 0}
                  </td>
                  <td
                    className="px-4 py-3 text-right tabular-nums text-zinc-100"
                    data-testid={`registrations-${row.code}`}
                  >
                    {row.registrations_count ?? 0}
                  </td>
                  <td
                    className="px-4 py-3 text-right tabular-nums text-emerald-300"
                    data-testid={`verified-${row.code}`}
                  >
                    {row.email_confirmed_count ?? 0}
                  </td>
                  <td
                    className="px-4 py-3 text-right tabular-nums text-amber-300"
                    data-testid={`pro-${row.code}`}
                  >
                    {row.pro_users_count ?? 0}
                  </td>
                  <td
                    className={`px-4 py-3 text-right tabular-nums ${
                      row.performance_tier === "high" ? "text-emerald-300"
                        : row.performance_tier === "medium" ? "text-amber-300"
                        : "text-zinc-500"
                    }`}
                    data-testid={`conv-rate-${row.code}`}
                  >
                    {fmtPct(row.click_to_pro_rate)}
                  </td>
                  <td className="px-4 py-3">
                    <StatusPill status={row.status} />
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => setMagicLinkCode(row.code)}
                        disabled={row.status !== "active"}
                        data-testid={`magic-link-${row.code}`}
                        title={row.status === "active"
                          ? "Magic Link + QR-Code anzeigen"
                          : "Code ist deaktiviert"}
                        className="rounded-md p-1.5 text-emerald-400 hover:text-emerald-300 hover:bg-zinc-800 disabled:opacity-30 disabled:hover:bg-transparent disabled:cursor-not-allowed"
                      >
                        <QrCode className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          setEditTarget({ code: row.code, note: row.note })
                        }
                        data-testid={`edit-note-${row.code}`}
                        title="Notiz bearbeiten"
                        className="rounded-md p-1.5 text-zinc-500 hover:text-zinc-100 hover:bg-zinc-800"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          toggleMutation.mutate({
                            code: row.code,
                            currentStatus: row.status,
                          })
                        }
                        disabled={toggleMutation.isPending}
                        data-testid={`toggle-status-${row.code}`}
                        title={row.status === "active" ? "Deaktivieren" : "Aktivieren"}
                        className={`rounded-md p-1.5 hover:bg-zinc-800 ${
                          row.status === "active"
                            ? "text-amber-400 hover:text-amber-300"
                            : "text-emerald-400 hover:text-emerald-300"
                        }`}
                      >
                        <Power className="w-4 h-4" />
                      </button>
                      {(() => {
                        // Hard-delete only offered when the code has
                        // never been used (server enforces same check).
                        const unused =
                          (row.clicks_count ?? 0) === 0
                          && (row.registrations_count ?? 0) === 0
                          && (row.conversions_count ?? 0) === 0;
                        if (!unused) return null;
                        return (
                          <button
                            type="button"
                            onClick={() => {
                              if (window.confirm(
                                `Code ${row.code} wirklich endgültig löschen? `
                                + `Diese Aktion ist nicht rückgängig zu machen.`,
                              )) {
                                hardDeleteMutation.mutate(row.code);
                              }
                            }}
                            disabled={hardDeleteMutation.isPending}
                            data-testid={`hard-delete-${row.code}`}
                            title="Endgültig löschen (nur ungenutzt)"
                            className="rounded-md p-1.5 text-rose-400 hover:text-rose-300 hover:bg-zinc-800"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        );
                      })()}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <NewCodeModal open={modalOpen} onClose={() => setModalOpen(false)} />
      <EditNoteModal
        open={!!editTarget}
        code={editTarget?.code}
        initialNote={editTarget?.note}
        onClose={() => setEditTarget(null)}
      />
      <MagicLinkModal
        open={!!magicLinkCode}
        code={magicLinkCode}
        onClose={() => setMagicLinkCode(null)}
      />
    </div>
  );
}
